import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  ReferenceLine
} from 'recharts';
import azureDevOpsService from './services/azureDevOpsService';
import ExpandableChartCard from './ExpandableChartCard';

const PLANNED_RELEASE = '26.1.0';
const ROOT_AREA_PATH = 'Fabrique';
const DELIVERY_DATE = (() => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 7);
  return date;
})();
const DAILY_START_DATE = new Date('2026-04-13T00:00:00');
const COLORS = ['#2563eb', '#0f766e', '#ca8a04', '#dc2626', '#7c3aed', '#ea580c', '#0891b2', '#4f46e5'];
const CLOSED_STATES = new Set([
  '4- doc',
  '4-doc',
  '5- validation',
  '5-validation',
  '6- done',
  '6-done',
  '6- cancelled',
  '6-cancelled',
  'done',
  'closed',
  'resolved',
  'removed'
]);
const PRIORITY_LABELS = {
  1: { label: 'P1', color: '#dc2626' },
  2: { label: 'P2', color: '#ea580c' },
  3: { label: 'P3', color: '#ca8a04' },
  4: { label: 'P4', color: '#2563eb' },
};
const DAILY_TYPE_BUCKETS = [
  { dataKey: 'pbi', name: 'PBI', fill: '#2563eb' },
  { dataKey: 'bug', name: 'Bug', fill: '#dc2626' },
  { dataKey: 'demandeClient', name: 'Demande Client', fill: '#ca8a04' },
  { dataKey: 'detteTechnique', name: 'Dette technique', fill: '#7c3aed' },
  { dataKey: 'autres', name: 'Autres', fill: '#0891b2' },
];

// Jours fériés français (année 2025 et 2026)
const FRENCH_HOLIDAYS = new Set([
  // 2025
  '2025-01-01', '2025-04-21', '2025-05-01', '2025-05-08',
  '2025-05-29', '2025-06-09', '2025-07-14', '2025-08-15',
  '2025-11-01', '2025-11-11', '2025-12-25',
  // 2026
  '2026-01-01', '2026-04-06', '2026-05-01', '2026-05-08',
  '2026-05-14', '2026-05-25', '2026-07-14', '2026-08-15',
  '2026-11-01', '2026-11-11', '2026-12-25',
]);

function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isWorkingDay(date) {
  const dow = date.getDay(); // 0=Sun, 6=Sat
  if (dow === 0 || dow === 6) return false;
  return !FRENCH_HOLIDAYS.has(toDateKey(date));
}

function addWorkingDays(date, n) {
  let current = new Date(date);
  let remaining = n;
  while (remaining > 0) {
    current.setDate(current.getDate() + 1);
    if (isWorkingDay(current)) remaining--;
  }
  return current;
}

function toDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function addDays(date, days) {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
}

function formatDayLabel(date) {
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function getDailyTypeBucket(workItemType) {
  const label = formatTypeLabel(workItemType);
  if (label === 'PBI') return 'pbi';
  if (label === 'Bug') return 'bug';
  if (label === 'Demande Client') return 'demandeClient';
  if (label === 'Dette technique') return 'detteTechnique';
  return 'autres';
}

function buildDailyStockTimelineData(items, startDate, deliveryDate, resolvedDatesMap = new Map()) {
  if (!items.length) return [];

  const firstDay = startOfDay(startDate);
  const today = startOfDay(new Date());
  // Rows go up to at least the delivery date so the reference line + trend have room
  const endDay = startOfDay(new Date(Math.max(today.getTime(), deliveryDate.getTime())));

  const createdTs = items
    .map(item => toDate(item.createdDate))
    .filter(Boolean)
    .map(d => startOfDay(d).getTime());
  // Un ticket est considéré résolu à la 1ère date de passage en état >= "4- Doc"
  // (priorité : 4- Doc > 5- Validation > 6- Done / 6- Cancelled)
  // Les tickets réouverts (état actuel non fermé) sont exclus : ils restent dans le stock.
  const closedTs = items
    .filter(item => isClosedTicket(item))
    .map(item => resolvedDatesMap.get(item.id) || null)
    .filter(Boolean)
    .map(d => startOfDay(d).getTime());

  const rows = [];
  for (let cursor = new Date(firstDay); cursor <= endDay; cursor = addDays(cursor, 1)) {
    if (!isWorkingDay(cursor)) continue;
    const ts = cursor.getTime();
    const isPast = ts <= today.getTime();
    const cumulativeCreated = isPast ? createdTs.filter(t => t <= ts).length : null;
    const cumulativeClosed = isPast ? closedTs.filter(t => t <= ts).length : null;
    rows.push({
      dayStart: new Date(cursor),
      day: formatDayLabel(cursor),
      cumulativeCreated,
      cumulativeClosed,
      remaining: isPast ? cumulativeCreated - cumulativeClosed : null,
      isDeliveryDay: cursor.getTime() === startOfDay(deliveryDate).getTime(),
    });
  }
  return rows;
}

function buildLinearRegression(points) {
  const n = points.length;
  if (n < 2) return null;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

function addTrendToTimelineData(rows, deliveryDate, trendWindowDays = 5) {
  if (rows.length < 2) return rows;

  const trendCutoff = addDays(startOfDay(new Date()), -1); // J-1: ignore today's partial progress
  const realRows = rows.filter(row => row.dayStart.getTime() <= trendCutoff.getTime() && row.remaining !== null);
  if (realRows.length < 2) return rows;

  // Build regression using real indices within the full rows array so projection is consistent
  const realIndices = rows.reduce((acc, row, i) => {
    if (row.dayStart.getTime() <= trendCutoff.getTime() && row.remaining !== null) acc.push(i);
    return acc;
  }, []);
  const window = Math.max(2, trendWindowDays);
  const windowIndices = realIndices.slice(-window);
  const points = windowIndices.map(i => ({ x: i, y: rows[i].remaining }));
  const reg = buildLinearRegression(points);
  if (!reg) return rows;

  const { slope, intercept } = reg;

  // Apply trend to all existing rows
  const enriched = rows.map((row, i) => ({
    ...row,
    trend: Math.max(0, Math.round(slope * i + intercept)),
  }));

  if (slope >= 0) return enriched;

  // Extrapolate beyond known rows until trend reaches 0, capped at delivery date
  const zeroIndex = Math.ceil(-intercept / slope);
  const lastIndex = enriched.length - 1;
  if (zeroIndex <= lastIndex) return enriched;

  const deliveryTs = startOfDay(deliveryDate).getTime();
  const lastRow = enriched[enriched.length - 1];
  const cap = Math.min(zeroIndex, lastIndex + 365);
  const projected = [];
  for (let i = lastIndex + 1; i <= cap; i++) {
    const dayStart = addWorkingDays(lastRow.dayStart, i - lastIndex);
    // Stop projecting beyond delivery date
    if (dayStart.getTime() > deliveryTs) break;
    projected.push({
      dayStart,
      day: formatDayLabel(dayStart),
      cumulativeCreated: null,
      cumulativeClosed: null,
      remaining: null,
      trend: Math.max(0, Math.round(slope * i + intercept)),
      isDeliveryDay: dayStart.getTime() === deliveryTs,
      isProjected: true,
    });
  }
  return [...enriched, ...projected];
}

function buildDailyOpenTimelineData(items, startDate, deliveryDate) {
  const firstDay = startOfDay(startDate);
  const endDay = startOfDay(new Date(Math.max(Date.now(), deliveryDate.getTime())));

  const rows = [];
  for (let cursor = new Date(firstDay); cursor <= endDay; cursor = addDays(cursor, 1)) {
    rows.push({
      dayStart: new Date(cursor),
      day: formatDayLabel(cursor),
      opened: 0,
      pbi: 0,
      bug: 0,
      demandeClient: 0,
      detteTechnique: 0,
      autres: 0,
      isDeliveryDay: cursor.getTime() === startOfDay(deliveryDate).getTime()
    });
  }

  const dayIndexByKey = new Map(rows.map((row, index) => [row.dayStart.toISOString(), index]));
  items.forEach(item => {
    const createdDate = toDate(item.createdDate);
    if (!createdDate) return;
    const dayKey = startOfDay(createdDate).toISOString();
    const rowIndex = dayIndexByKey.get(dayKey);
    if (rowIndex != null) {
      rows[rowIndex].opened += 1;
      const bucket = getDailyTypeBucket(item.workItemType);
      rows[rowIndex][bucket] += 1;
    }
  });

  return rows;
}

function formatTypeLabel(workItemType) {
  const normalized = String(workItemType || 'Non renseigne').trim();

  if (normalized === 'Product Backlog Item') return 'PBI';
  if (normalized === 'Bug') return 'Bug';
  if (normalized === 'Demande Client') return 'Demande Client';
  if (normalized === 'Dette technique') return 'Dette technique';

  return normalized || 'Non renseigne';
}

function formatAreaPath(areaPath) {
  const value = String(areaPath || 'Non renseigne').trim();
  const prefixes = [
    'Fabrique\\Tribus\\Tribu Argonos\\Squads\\',
    'Fabrique\\Tribus\\Tribu Argonos\\',
    'Fabrique\\'
  ];

  const prefix = prefixes.find(candidate => value.startsWith(candidate));
  if (!prefix) {
    return value;
  }

  const normalized = value.slice(prefix.length);
  const rootSegment = normalized.split('\\').filter(Boolean)[0];

  return rootSegment || normalized || 'Non renseigne';
}

function buildCountRows(items, getKey) {
  const counts = new Map();

  items.forEach(item => {
    const key = getKey(item);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((left, right) => {
      if (right.count !== left.count) return right.count - left.count;
      return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
    });
}

function buildStatusCountRows(items, getKey) {
  const counts = new Map();
  items.forEach(item => {
    const key = getKey(item);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
}

function buildAreaTypeRows(items, typeKeys) {
  const rows = new Map();

  items.forEach(item => {
    const areaLabel = formatAreaPath(item.areaPath);
    const typeLabel = formatTypeLabel(item.workItemType);

    if (!rows.has(areaLabel)) {
      rows.set(areaLabel, {
        areaPath: areaLabel,
        fullAreaPath: item.areaPath || 'Non renseigne',
        total: 0
      });
    }

    const row = rows.get(areaLabel);
    row[typeLabel] = (row[typeLabel] || 0) + 1;
    row.total += 1;
  });

  return Array.from(rows.values())
    .map(row => {
      typeKeys.forEach(typeKey => {
        row[typeKey] = row[typeKey] || 0;
      });
      return row;
    })
    .sort((left, right) => {
      if (right.total !== left.total) return right.total - left.total;
      return left.areaPath.localeCompare(right.areaPath, undefined, { sensitivity: 'base' });
    });
}

function isClosedTicket(item) {
  const normalizedState = String(item.state || '').trim().toLowerCase();
  // Vérifier d'abord l'état actuel — un ticket réouvert (ex: 2- Dev) après résolution
  // conserve un closedDate dans Azure DevOps mais n'est plus fermé.
  if (!CLOSED_STATES.has(normalizedState)) return false;
  return true;
}

function isDoneState(state) {
  return String(state || '').trim().toLowerCase().includes('done');
}

function StockTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const visible = payload.filter(p => p.value !== null && p.value !== undefined);
  if (!visible.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-sm">
      <div className="font-semibold text-gray-900 mb-1">{label}</div>
      {visible.map(p => (
        <div key={p.dataKey} style={{ color: p.stroke || p.color }}>
          {p.name}: <span className="font-medium">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function DailyOpenTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-sm">
      <div className="font-semibold text-gray-900 mb-1">{label}</div>
      <div className="text-gray-700">Total ouverts: <span className="font-medium">{point.opened}</span></div>
      <div className="text-gray-700">PBI: <span className="font-medium">{point.pbi}</span></div>
      <div className="text-gray-700">Bugs: <span className="font-medium">{point.bug}</span></div>
      <div className="text-gray-700">Demande Client: <span className="font-medium">{point.demandeClient}</span></div>
      <div className="text-gray-700">Dette technique: <span className="font-medium">{point.detteTechnique}</span></div>
      <div className="text-gray-700">Autres: <span className="font-medium">{point.autres}</span></div>
    </div>
  );
}

// Builds one remaining-per-day series per squad for bugs
function buildSquadBugStockData(bugItems, squads, startDate, deliveryDate, resolvedDatesMap) {
  const firstDay = startOfDay(startDate);
  const today = startOfDay(new Date());
  const endDay = startOfDay(new Date(Math.max(today.getTime(), deliveryDate.getTime())));

  // Precompute per-squad createdTs and closedTs
  const squadData = squads.map(squad => {
    const squadBugs = bugItems.filter(item => formatAreaPath(item.areaPath) === squad);
    const createdTs = squadBugs
      .map(item => toDate(item.createdDate))
      .filter(Boolean)
      .map(d => startOfDay(d).getTime());
    const closedTs = squadBugs
      .filter(item => isClosedTicket(item))
      .map(item => resolvedDatesMap.get(item.id) || null)
      .filter(Boolean)
      .map(d => startOfDay(d).getTime());
    return { squad, squadBugs, createdTs, closedTs };
  });

  const rows = [];
  for (let cursor = new Date(firstDay); cursor <= endDay; cursor = addDays(cursor, 1)) {
    if (!isWorkingDay(cursor)) continue;
    const ts = cursor.getTime();
    const isPast = ts <= today.getTime();
    const row = {
      dayStart: new Date(cursor),
      day: formatDayLabel(cursor),
      isDeliveryDay: cursor.getTime() === startOfDay(deliveryDate).getTime(),
    };
    squadData.forEach(({ squad, createdTs, closedTs }) => {
      if (isPast) {
        const created = createdTs.filter(t => t <= ts).length;
        const closed = closedTs.filter(t => t <= ts).length;
        row[squad] = created - closed;
      } else {
        row[squad] = null;
      }
    });
    rows.push(row);
  }
  return rows;
}

// Teintes catégorielles bien séparées, dans un ordre fixe : l'ancienne
// palette mettait bleu, indigo et violet côte à côte, indiscernables sur les courbes.
// 12 teintes : au-delà, les couleurs reviennent et deux squads se confondent (11 en 26.2).
const SQUAD_COLORS = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#ec4899', '#008300',
  '#9333ea', '#b91c1c', '#7b4a12', '#0b3d91', '#111827', '#6b7280',
];

// La couleur suit la squad, pas son rang dans la liste filtrée : sinon filtrer repeint les courbes.
function getSquadColor(squad, allSquads) {
  const idx = allSquads.indexOf(squad);
  return SQUAD_COLORS[(idx >= 0 ? idx : 0) % SQUAD_COLORS.length];
}

function getStatusLabel(item) {
  return item.state || 'Non renseigne';
}

function getAvailableStatuses(items) {
  return [...new Set(items.map(getStatusLabel))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
}

// Filtre sur l'état actuel du ticket. Les états fermés sont en vert : les décocher
// (ex. 6- Cancelled) retire ces tickets des courbes au lieu de les compter comme clôturés.
function StatusFilterChips({ statuses, selected, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Statut :</span>
      {statuses.map(status => {
        const color = CLOSED_STATES.has(status.trim().toLowerCase()) ? '#16a34a' : '#475569';
        const isActive = selected.length === 0 || selected.includes(status);
        return (
          <button
            key={status}
            onClick={() => {
              onChange(prev => {
                if (prev.length === 0) return [status];
                const next = prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status];
                return next.length === statuses.length ? [] : next;
              });
            }}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              isActive ? 'text-white border-transparent' : 'bg-white text-gray-400 border-gray-200'
            }`}
            style={isActive ? { backgroundColor: color, borderColor: color } : {}}
          >
            {status}
          </button>
        );
      })}
      {selected.length > 0 && (
        <button onClick={() => onChange([])} className="px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">✕</button>
      )}
    </div>
  );
}

function BugPopup({ point, squads, allSquads = squads, bugItems, resolvedDatesMap, isResolved, onClose, label = 'Bugs', priorityFilter = [] }) {
  const [sort, setSort] = useState(null); // { key, dir } — null = ordre par défaut (squad puis priorité)
  if (!point || !squads?.length) return null;
  const ts = point.dayStart.getTime();
  const multiSquad = squads.length > 1;
  const showSeverityColumn = String(label || '').trim().toLowerCase() === 'bugs';
  const isClosedAtPoint = (item) => {
    if (!isClosedTicket(item)) return false;
    const resolved = resolvedDatesMap.get(item.id);
    return !!resolved && startOfDay(resolved).getTime() <= ts;
  };

  const gridCols = multiSquad
    ? (showSeverityColumn
      ? 'grid-cols-[90px_60px_1fr_130px_120px_120px]'
      : 'grid-cols-[90px_60px_1fr_150px_120px]')
    : (showSeverityColumn
      ? 'grid-cols-[90px_60px_1fr_120px_120px]'
      : 'grid-cols-[90px_60px_1fr_120px]');

  const bugs = bugItems
    .filter(item => {
      if (!squads.includes(formatAreaPath(item.areaPath))) return false;
      const created = toDate(item.createdDate);
      if (!created || startOfDay(created).getTime() > ts) return false;
      if (isClosedAtPoint(item)) return false;
      if (priorityFilter.length > 0 && !priorityFilter.includes(item.priority ?? null)) return false;
      return true;
    })
    .sort((a, b) => {
      // Sort by squad first (when multi), then priority
      if (multiSquad) {
        const si = squads.indexOf(formatAreaPath(a.areaPath));
        const sj = squads.indexOf(formatAreaPath(b.areaPath));
        if (si !== sj) return si - sj;
      }
      return (a.priority ?? 99) - (b.priority ?? 99);
    });

  // Tri par colonne : le tri JS étant stable, l'ordre par défaut départage les ex-aequo.
  if (sort) {
    const sortValue = {
      priority: item => item.priority ?? null,
      id: item => Number(item.id),
      title: item => item.title || '',
      squad: item => formatAreaPath(item.areaPath) || '',
      severity: item => item.severity || '',
      state: item => item.state || '',
    }[sort.key];
    bugs.sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      // Valeurs absentes toujours en fin de liste, quel que soit le sens
      const aEmpty = av === null || av === '';
      const bEmpty = bv === null || bv === '';
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : (aEmpty ? 1 : -1);
      const cmp = typeof av === 'number'
        ? av - bv
        : av.localeCompare(bv, undefined, { sensitivity: 'base', numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }

  const toggleSort = (key) => {
    setSort(prev => (prev?.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));
  };

  const renderSortHeader = (key, text) => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className="flex items-center text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-800 transition-colors"
    >
      {text}
      {sort?.key === key
        ? <span className="ml-1">{sort.dir === 'asc' ? '↑' : '↓'}</span>
        : <span className="text-gray-300 ml-1">↕</span>}
    </button>
  );

  const title = multiSquad ? squads.join(' · ') : squads[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <div className="text-lg font-semibold text-gray-900">{title}</div>
            <div className="text-sm text-gray-500 mt-0.5">
              {label} ouverts au <span className="font-medium text-gray-700">{point.day}</span>
              {' '}—{' '}
              <span className="font-medium text-gray-700">{bugs.length}</span> ticket{bugs.length > 1 ? 's' : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors text-lg leading-none"
          >✕</button>
        </div>

        {/* Table header */}
        {bugs.length > 0 && (
          <div className={`grid ${gridCols} gap-x-3 px-6 py-2 bg-gray-50 border-b border-gray-200 shrink-0`}>
            <div>{renderSortHeader('priority', 'Priorité')}</div>
            <div>{renderSortHeader('id', 'ID')}</div>
            <div>{renderSortHeader('title', 'Titre')}</div>
            {multiSquad && <div>{renderSortHeader('squad', 'Squad')}</div>}
            {showSeverityColumn && <div>{renderSortHeader('severity', 'Sévérité')}</div>}
            <div>{renderSortHeader('state', 'Statut')}</div>
          </div>
        )}

        {/* Rows */}
        <div className="overflow-y-auto flex-1">
          {bugs.length === 0 ? (
            <div className="px-6 py-8 text-gray-400 text-sm text-center">Aucun {label.toLowerCase()} ouvert à cette date.</div>
          ) : bugs.map((bug, i) => {
            const prioColor = bug.priority != null
              ? (PRIORITY_LABELS[bug.priority]?.color ?? '#6b7280')
              : '#9ca3af';
            const prioLabel = bug.priority != null
              ? (PRIORITY_LABELS[bug.priority]?.label ?? `P${bug.priority}`)
              : '—';
            const bugSquad = formatAreaPath(bug.areaPath);
            const squadColor = getSquadColor(bugSquad, allSquads);
            return (
              <div
                key={bug.id}
                className={`grid ${gridCols} gap-x-3 px-6 py-3 items-start ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
              >
                {/* Priorité */}
                <div className="flex items-center pt-0.5">
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{ backgroundColor: prioColor + '18', color: prioColor, border: `1px solid ${prioColor}40` }}
                  >
                    {prioLabel}
                  </span>
                </div>
                {/* ID */}
                <div className="pt-0.5">
                  <a
                    href={bug.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-mono font-medium"
                  >
                    #{bug.id}
                  </a>
                </div>
                {/* Titre */}
                <div className="text-sm text-gray-800 leading-snug">{bug.title}</div>
                {/* Squad (multi seulement) */}
                {multiSquad && (
                  <div className="pt-0.5">
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: squadColor + '18', color: squadColor, border: `1px solid ${squadColor}40` }}
                    >
                      {bugSquad}
                    </span>
                  </div>
                )}
                {/* Sévérité (bugs seulement) */}
                {showSeverityColumn && (
                  <div className="text-xs text-gray-500 pt-0.5 truncate">{bug.severity || '—'}</div>
                )}
                {/* Statut */}
                <div className="text-xs text-gray-500 pt-0.5 truncate">{bug.state}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function KpiPlannedRelease261() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [resolvedDatesMap, setResolvedDatesMap] = useState(new Map());
  const [plannedReleaseFieldName, setPlannedReleaseFieldName] = useState(null);
  const [bugPriorityFilter, setBugPriorityFilter] = useState([]);
  const [bugStatusFilter, setBugStatusFilter] = useState([]);
  const [pbiStatusFilter, setPbiStatusFilter] = useState([]);
  const [squadFilter, setSquadFilter] = useState([]);
  const [squadPopup, setSquadPopup] = useState(null); // { point, squad }
  const [pbiSquadFilter, setPbiSquadFilter] = useState([]);
  const [pbiSquadPopup, setPbiSquadPopup] = useState(null); // { point, squad }
  const [pbiTrendWindowDays, setPbiTrendWindowDays] = useState(5);
  const [bugTrendWindowDays, setBugTrendWindowDays] = useState(5);

  const deliveryDate = DELIVERY_DATE;

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const result = await azureDevOpsService.getWorkItemsByPlannedReleaseContains(PLANNED_RELEASE, {
          areaPath: ROOT_AREA_PATH
        });

        if (cancelled) return;

        const fetchedItems = result.items || [];
        setItems(fetchedItems);
        setPlannedReleaseFieldName(result.plannedReleaseFieldName || null);

        // Récupérer l'historique des états pour construire les dates de résolution
        const allIds = fetchedItems.map(item => item.id).filter(Boolean);
        const datesMap = await azureDevOpsService.getFirstResolvedDates(allIds);
        if (!cancelled) setResolvedDatesMap(datesMap);
      } catch (fetchError) {
        if (cancelled) return;
        console.error('Erreur chargement KPI planned release 26.1.0:', fetchError);
        setError(fetchError.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  // Garder l'historique complet pour les graphes de stock afin de conserver les clotures.
  const pbiItems = items.filter(item => item.workItemType === 'Product Backlog Item');
  const bugItems = items.filter(item => item.workItemType === 'Bug');

  const availableBugPriorities = [...new Set(bugItems.map(item => item.priority ?? null))]
    .sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a - b;
    });

  const availableBugStatuses = getAvailableStatuses(bugItems);
  const availablePbiStatuses = getAvailableStatuses(pbiItems);

  const filteredBugItems = bugItems
    .filter(item => bugPriorityFilter.length === 0 || bugPriorityFilter.includes(item.priority ?? null))
    .filter(item => bugStatusFilter.length === 0 || bugStatusFilter.includes(getStatusLabel(item)))
    .filter(item => squadFilter.length === 0 || squadFilter.includes(formatAreaPath(item.areaPath)));

  const filteredPbiItems = pbiItems
    .filter(item => pbiStatusFilter.length === 0 || pbiStatusFilter.includes(getStatusLabel(item)))
    .filter(item => pbiSquadFilter.length === 0 || pbiSquadFilter.includes(formatAreaPath(item.areaPath)));

  // Un ticket est "ouvert" s'il n'a pas encore atteint un état >= 4- Doc (resolvedDatesMap)
  // Un ticket réouvert (closedDate présent mais état actuel non fermé) doit être considéré comme ouvert.
  const isResolved = (item) => {
    if (!isClosedTicket(item)) return false; // réouvert : état actuel non fermé → ouvert quoi qu'il en soit
    return resolvedDatesMap.size > 0
      ? resolvedDatesMap.has(item.id)
      : true; // fallback pendant le chargement des historiques
  };

  const openItems = items.filter(item => !isResolved(item));

  const typeData = buildCountRows(openItems, item => formatTypeLabel(item.workItemType));
  const typeKeys = typeData.map(row => row.name);
  const areaData = buildCountRows(openItems, item => formatAreaPath(item.areaPath)).map(row => ({
    ...row,
    fullAreaPath: openItems.find(item => formatAreaPath(item.areaPath) === row.name)?.areaPath || row.name
  }));
  const areaTypeData = buildAreaTypeRows(openItems, typeKeys);
  const topAreaTypeData = areaTypeData.slice(0, 12);
  const dailyTimelineData = buildDailyOpenTimelineData(openItems, DAILY_START_DATE, deliveryDate);

  const bugsByStatusData = buildStatusCountRows(
    filteredBugItems.filter(item => !isResolved(item)),
    item => item.state || 'Non renseigne'
  );

  const pbiByStatusData = buildStatusCountRows(
    filteredPbiItems.filter(item => !isResolved(item)),
    item => item.state || 'Non renseigne'
  );

  // Breakdown des états pour les infos des graphes stock
  const pbiResolved = filteredPbiItems.filter(item => isResolved(item));
  const pbiOpen = filteredPbiItems.filter(item => !isResolved(item));
  const pbiStateGroups = [...new Set(pbiOpen.map(item => item.state).filter(Boolean))].sort();

  const bugResolved = filteredBugItems.filter(item => isResolved(item));
  const bugOpen = filteredBugItems.filter(item => !isResolved(item));
  const bugStateGroups = [...new Set(bugOpen.map(item => item.state).filter(Boolean))].sort();
  const bugPriorityGroups = bugPriorityFilter.length > 0
    ? bugPriorityFilter.map(p => p !== null
        ? (PRIORITY_LABELS[p]?.label ?? `P${p}`)
        : 'Non définie').join(', ')
    : 'Toutes';

  // Breakdown par type des tickets ouverts (tous types confondus)
  const openTypeBreakdown = typeData.map(row => `${row.name} : ${row.count}`).join(' · ');

  // Graphe stock bugs par squad
  const availableSquads = areaData.map(row => row.name); // mêmes squads que "Types par area path"
  const activeSquads = squadFilter.length > 0 ? squadFilter : availableSquads;
  const squadBugStockData = filteredBugItems.length
    ? buildSquadBugStockData(filteredBugItems, activeSquads, DAILY_START_DATE, deliveryDate, resolvedDatesMap)
    : [];

  // Graphe stock PBI par squad
  const activePbiSquads = pbiSquadFilter.length > 0 ? pbiSquadFilter : availableSquads;
  const squadPbiStockData = filteredPbiItems.length
    ? buildSquadBugStockData(filteredPbiItems, activePbiSquads, DAILY_START_DATE, deliveryDate, resolvedDatesMap)
    : [];

  const pbiStockData = addTrendToTimelineData(
    buildDailyStockTimelineData(filteredPbiItems, DAILY_START_DATE, deliveryDate, resolvedDatesMap),
    deliveryDate,
    pbiTrendWindowDays
  );
  const bugStockData = addTrendToTimelineData(
    buildDailyStockTimelineData(filteredBugItems, DAILY_START_DATE, deliveryDate, resolvedDatesMap),
    deliveryDate,
    bugTrendWindowDays
  );

  const realPbiRows = pbiStockData.filter(r => r.remaining !== null && !r.isProjected);
  const realBugRows = bugStockData.filter(r => r.remaining !== null && !r.isProjected);
  const remainingPbiNow = realPbiRows.length ? realPbiRows[realPbiRows.length - 1].remaining : filteredPbiItems.length;
  const remainingBugNow = realBugRows.length ? realBugRows[realBugRows.length - 1].remaining : filteredBugItems.length;

  const summary = {
    total: openItems.length,
    types: typeData.length,
    areaPaths: areaData.length,
    mainType: typeData[0]?.name || 'Aucun',
    remainingPbi: remainingPbiNow,
    remainingBug: remainingBugNow
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-2">KPI 26.1.0</h2>
        <div className="text-gray-500">Chargement des tickets non fermés avec planned release 26.1.0...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-2">KPI 26.1.0</h2>
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded">
          Erreur : {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">KPI 26.1.0</h2>
            <p className="text-gray-600 mt-1">
              Tickets dont le planned release contient <strong>{PLANNED_RELEASE}</strong>, périmètre <strong>{ROOT_AREA_PATH}</strong>.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Critère résolution : 1re date de passage en <em>4- Doc</em>, sinon <em>5- Validation</em>, sinon <em>6- Done / 6- Cancelled</em>.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <div className="text-sm uppercase tracking-wide text-gray-500">Tickets ouverts</div>
          <div className="text-3xl font-semibold text-gray-900 mt-2">{summary.total}</div>
          {openTypeBreakdown && (
            <div className="text-xs text-gray-500 mt-2 leading-relaxed">{openTypeBreakdown}</div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <div className="text-sm uppercase tracking-wide text-gray-500">Reste PBI</div>
          <div className="text-3xl font-semibold text-gray-900 mt-2">{summary.remainingPbi}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <div className="text-sm uppercase tracking-wide text-gray-500">Reste Bugs</div>
          <div className="text-3xl font-semibold text-gray-900 mt-2">{summary.remainingBug}</div>
        </div>
      </div>

      {/* Ligne 1 : Stock PBI + Stock PBI par Squad — filtres communs */}
      {(availablePbiStatuses.length > 1 || availableSquads.length > 1) && (
        <div className="bg-white rounded-lg shadow px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-3">
          {availablePbiStatuses.length > 1 && (
            <StatusFilterChips statuses={availablePbiStatuses} selected={pbiStatusFilter} onChange={setPbiStatusFilter} />
          )}
          {availablePbiStatuses.length > 1 && availableSquads.length > 1 && (
            <div className="w-px h-6 bg-gray-200 shrink-0" />
          )}
          {availableSquads.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Squad :</span>
              {availableSquads.map(squad => {
                const color = getSquadColor(squad, availableSquads);
                const isActive = pbiSquadFilter.length === 0 || pbiSquadFilter.includes(squad);
                return (
                  <button
                    key={squad}
                    onClick={() => {
                      setPbiSquadFilter(prev => {
                        if (prev.length === 0) return [squad];
                        const next = prev.includes(squad) ? prev.filter(s => s !== squad) : [...prev, squad];
                        return next.length === availableSquads.length ? [] : next;
                      });
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      isActive ? 'text-white border-transparent' : 'bg-white text-gray-400 border-gray-200'
                    }`}
                    style={isActive ? { backgroundColor: color, borderColor: color } : {}}
                  >
                    {squad}
                  </button>
                );
              })}
              {pbiSquadFilter.length > 0 && (
                <button onClick={() => setPbiSquadFilter([])} className="px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">✕</button>
              )}
            </div>
          )}
          {(pbiStatusFilter.length > 0 || pbiSquadFilter.length > 0) && (
            <>
              <div className="w-px h-6 bg-gray-200 shrink-0" />
              <button
                onClick={() => { setPbiStatusFilter([]); setPbiSquadFilter([]); }}
                className="px-3 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Tout afficher
              </button>
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ExpandableChartCard
          title="Stock restant dans le temps - PBI"
          subtitle={
            <div className="text-xs text-gray-500 space-y-0.5">
              <div>
                <span className="font-medium text-gray-700">Périmètre :</span> {filteredPbiItems.length} PBI au total —{' '}
                <span className="text-red-600 font-medium">{pbiOpen.length} ouverts</span>,{' '}
                <span className="text-green-600 font-medium">{pbiResolved.length} résolus</span>.
              </div>
              {pbiStatusFilter.length > 0 && (
                <div><span className="font-medium text-gray-700">Filtre statut :</span> {pbiStatusFilter.join(' · ')}</div>
              )}
              {pbiStateGroups.length > 0 && (
                <div><span className="font-medium text-gray-700">États ouverts :</span> {pbiStateGroups.join(' · ')}</div>
              )}
              <div>
                <span className="font-medium text-gray-700">Critère résolution :</span>{' '}
                1re date de passage en <em>4- Doc</em>, sinon <em>5- Validation</em>, sinon <em>6- Done / 6- Cancelled</em>.
              </div>
              <div className="text-gray-400">Jours ouvrés uniquement (hors week-ends et jours fériés français).</div>
            </div>
          }
          extraContent={
            <div className="mt-3 max-w-md">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Fenêtre de tendance PBI</span>
                <span className="font-medium">{pbiTrendWindowDays} jours ouvrés</span>
              </div>
              <input
                type="range"
                min={2}
                max={20}
                step={1}
                value={pbiTrendWindowDays}
                onChange={(e) => setPbiTrendWindowDays(Number(e.target.value))}
                className="w-full accent-blue-600"
                aria-label="Fenêtre de tendance PBI (jours ouvrés)"
              />
            </div>
          }
          normalHeight={340}
        >
          {(height) => pbiStockData.length ? (
            <ResponsiveContainer width="100%" height={height}>
              <LineChart data={pbiStockData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" interval={Math.max(0, Math.floor(pbiStockData.length / 8) - 1)} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip content={<StockTooltip />} />
                <Legend />
                <ReferenceLine
                  x={pbiStockData.find(row => row.isDeliveryDay)?.day}
                  stroke="#dc2626"
                  strokeDasharray="4 4"
                  label={{ value: 'Livraison cible', position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                />
                <Line type="monotone" dataKey="remaining" name="Restant" stroke="#dc2626" strokeWidth={3} dot={{ r: 2 }} connectNulls={false} />
                <Line type="monotone" dataKey="cumulativeCreated" name="Cumul créés" stroke="#2563eb" strokeWidth={2} dot={false} connectNulls={false} />
                <Line type="monotone" dataKey="cumulativeClosed" name="Cumul clôturés" stroke="#16a34a" strokeWidth={2} dot={false} connectNulls={false} />
                <Line type="monotone" dataKey="trend" name={`Tendance (${pbiTrendWindowDays}j)`} stroke="#7c3aed" strokeWidth={2} strokeDasharray="6 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500">Pas assez de dates pour tracer une évolution sur les PBI.</div>
          )}
        </ExpandableChartCard>

        {/* STOCK PBI PAR SQUAD */}
        <ExpandableChartCard
          title="Stock de PBI par Squad dans le temps"
          subtitle={
            <div className="text-xs text-gray-500 space-y-0.5">
              <div>PBI ouverts par squad (jours ouvrés). Cliquez sur un point pour voir la liste des PBI.</div>
              <div className="text-gray-400">Même critère de résolution que le graphe global.</div>
            </div>
          }
          normalHeight={380}
        >
          {(height) => squadPbiStockData.length ? (
            <>
              <ResponsiveContainer width="100%" height={height}>
                <LineChart
                  data={squadPbiStockData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" interval={Math.max(0, Math.floor(squadPbiStockData.length / 8) - 1)} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip content={<StockTooltip />} />
                  <Legend />
                  <ReferenceLine
                    x={squadPbiStockData.find(r => r.isDeliveryDay)?.day}
                    stroke="#dc2626"
                    strokeDasharray="4 4"
                    label={{ value: 'Livraison cible', position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                  />
                  {activePbiSquads.map(squad => (
                    <Line
                      key={squad}
                      type="monotone"
                      dataKey={squad}
                      name={squad}
                      stroke={getSquadColor(squad, availableSquads)}
                      strokeWidth={2}
                      dot={{ r: 3, cursor: 'pointer' }}
                      activeDot={{ r: 5, cursor: 'pointer', onClick: (_e, dotData) => {
                        if (dotData?.payload && dotData?.dataKey) {
                          const clickedVal = dotData.payload[dotData.dataKey];
                          const overlapping = activePbiSquads.filter(s => dotData.payload[s] === clickedVal && dotData.payload[s] != null);
                          setPbiSquadPopup({ point: dotData.payload, squads: overlapping.length > 1 ? overlapping : [dotData.dataKey] });
                        }
                      }}}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              {pbiSquadPopup && (
                <BugPopup
                  point={pbiSquadPopup.point}
                  squads={pbiSquadPopup.squads}
                  allSquads={availableSquads}
                  bugItems={filteredPbiItems}
                  resolvedDatesMap={resolvedDatesMap}
                  isResolved={isResolved}
                  onClose={() => setPbiSquadPopup(null)}
                  label="PBI"
                />
              )}
            </>
          ) : (
            <div className="text-gray-500">Pas assez de données pour tracer l'évolution par squad.</div>
          )}
        </ExpandableChartCard>
      </div>

      {/* Ligne 2 : Stock Bugs + Stock Bugs par Squad — filtres communs */}
      {(availableBugPriorities.length > 0 || availableBugStatuses.length > 1 || availableSquads.length > 1) && (
        <div className="bg-white rounded-lg shadow px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-3">
          {availableBugPriorities.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Priorité :</span>
              {availableBugPriorities.map(p => {
                const cfg = p !== null
                  ? (PRIORITY_LABELS[p] ?? { label: `P${p}`, color: '#6b7280' })
                  : { label: 'Non définie', color: '#6b7280' };
                const isActive = bugPriorityFilter.length === 0 || bugPriorityFilter.includes(p);
                return (
                  <button
                    key={String(p)}
                    onClick={() => {
                      setBugPriorityFilter(prev => {
                        if (prev.length === 0) return [p];
                        const next = prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p];
                        return next.length === availableBugPriorities.length ? [] : next;
                      });
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      isActive ? 'text-white border-transparent' : 'bg-white text-gray-400 border-gray-200'
                    }`}
                    style={isActive ? { backgroundColor: cfg.color, borderColor: cfg.color } : {}}
                  >
                    {cfg.label}
                  </button>
                );
              })}
              {bugPriorityFilter.length > 0 && (
                <button onClick={() => setBugPriorityFilter([])} className="px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">✕</button>
              )}
            </div>
          )}
          {availableBugPriorities.length > 0 && availableBugStatuses.length > 1 && (
            <div className="w-px h-6 bg-gray-200 shrink-0" />
          )}
          {availableBugStatuses.length > 1 && (
            <StatusFilterChips statuses={availableBugStatuses} selected={bugStatusFilter} onChange={setBugStatusFilter} />
          )}
          {(availableBugPriorities.length > 0 || availableBugStatuses.length > 1) && availableSquads.length > 1 && (
            <div className="w-px h-6 bg-gray-200 shrink-0" />
          )}
          {availableSquads.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Squad :</span>
              {availableSquads.map(squad => {
                const color = getSquadColor(squad, availableSquads);
                const isActive = squadFilter.length === 0 || squadFilter.includes(squad);
                return (
                  <button
                    key={squad}
                    onClick={() => {
                      setSquadFilter(prev => {
                        if (prev.length === 0) return [squad];
                        const next = prev.includes(squad) ? prev.filter(s => s !== squad) : [...prev, squad];
                        return next.length === availableSquads.length ? [] : next;
                      });
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      isActive ? 'text-white border-transparent' : 'bg-white text-gray-400 border-gray-200'
                    }`}
                    style={isActive ? { backgroundColor: color, borderColor: color } : {}}
                  >
                    {squad}
                  </button>
                );
              })}
              {squadFilter.length > 0 && (
                <button onClick={() => setSquadFilter([])} className="px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">✕</button>
              )}
            </div>
          )}
          {(bugPriorityFilter.length > 0 || bugStatusFilter.length > 0 || squadFilter.length > 0) && (
            <>
              <div className="w-px h-6 bg-gray-200 shrink-0" />
              <button
                onClick={() => { setBugPriorityFilter([]); setBugStatusFilter([]); setSquadFilter([]); }}
                className="px-3 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Tout afficher
              </button>
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ExpandableChartCard
          title="Stock restant dans le temps - Bugs"
          subtitle={
            <div className="text-xs text-gray-500 space-y-0.5">
              <div>
                <span className="font-medium text-gray-700">Filtre priorité :</span> {bugPriorityGroups} —{' '}
                {filteredBugItems.length} bugs (<span className="text-red-600 font-medium">{bugOpen.length} ouverts</span>,{' '}
                <span className="text-green-600 font-medium">{bugResolved.length} résolus</span>).
              </div>
              {bugStatusFilter.length > 0 && (
                <div><span className="font-medium text-gray-700">Filtre statut :</span> {bugStatusFilter.join(' · ')}</div>
              )}
              {bugStateGroups.length > 0 && (
                <div><span className="font-medium text-gray-700">États ouverts :</span> {bugStateGroups.join(' · ')}</div>
              )}
              <div>
                <span className="font-medium text-gray-700">Critère résolution :</span>{' '}
                1re date de passage en <em>4- Doc</em>, sinon <em>5- Validation</em>, sinon <em>6- Done / 6- Cancelled</em>.
              </div>
              <div className="text-gray-400">Jours ouvrés uniquement (hors week-ends et jours fériés français).</div>
            </div>
          }
          extraContent={
            <div className="mt-3 max-w-md">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Fenêtre de tendance Bugs</span>
                <span className="font-medium">{bugTrendWindowDays} jours ouvrés</span>
              </div>
              <input
                type="range"
                min={2}
                max={20}
                step={1}
                value={bugTrendWindowDays}
                onChange={(e) => setBugTrendWindowDays(Number(e.target.value))}
                className="w-full accent-blue-600"
                aria-label="Fenêtre de tendance Bugs (jours ouvrés)"
              />
            </div>
          }
          normalHeight={340}
        >
          {(height) => bugStockData.length ? (
            <ResponsiveContainer width="100%" height={height}>
              <LineChart data={bugStockData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" interval={Math.max(0, Math.floor(bugStockData.length / 8) - 1)} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip content={<StockTooltip />} />
                <Legend />
                <ReferenceLine
                  x={bugStockData.find(row => row.isDeliveryDay)?.day}
                  stroke="#dc2626"
                  strokeDasharray="4 4"
                  label={{ value: 'Livraison cible', position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                />
                <Line type="monotone" dataKey="remaining" name="Restant" stroke="#dc2626" strokeWidth={3} dot={{ r: 2 }} connectNulls={false} />
                <Line type="monotone" dataKey="cumulativeCreated" name="Cumul créés" stroke="#2563eb" strokeWidth={2} dot={false} connectNulls={false} />
                <Line type="monotone" dataKey="cumulativeClosed" name="Cumul clôturés" stroke="#16a34a" strokeWidth={2} dot={false} connectNulls={false} />
                <Line type="monotone" dataKey="trend" name={`Tendance (${bugTrendWindowDays}j)`} stroke="#7c3aed" strokeWidth={2} strokeDasharray="6 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500">Pas assez de dates pour tracer une évolution sur les Bugs.</div>
          )}
        </ExpandableChartCard>

        {/* STOCK BUGS PAR SQUAD */}
        <ExpandableChartCard
          title="Stock de Bugs par Squad dans le temps"
          subtitle={
            <div className="text-xs text-gray-500 space-y-0.5">
              <div>Bugs ouverts par squad (jours ouvrés). Cliquez sur un point pour voir la liste des bugs.</div>
              <div className="text-gray-400">Même critère de résolution que le graphe global.</div>
            </div>
          }
          normalHeight={380}
        >
          {(height) => squadBugStockData.length ? (
            <>
              <ResponsiveContainer width="100%" height={height}>
                <LineChart
                  data={squadBugStockData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" interval={Math.max(0, Math.floor(squadBugStockData.length / 8) - 1)} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip content={<StockTooltip />} />
                  <Legend />
                  <ReferenceLine
                    x={squadBugStockData.find(r => r.isDeliveryDay)?.day}
                    stroke="#dc2626"
                    strokeDasharray="4 4"
                    label={{ value: 'Livraison cible', position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                  />
                  {activeSquads.map(squad => (
                    <Line
                      key={squad}
                      type="monotone"
                      dataKey={squad}
                      name={squad}
                      stroke={getSquadColor(squad, availableSquads)}
                      strokeWidth={2}
                      dot={{ r: 3, cursor: 'pointer' }}
                      activeDot={{ r: 5, cursor: 'pointer', onClick: (_e, dotData) => {
                        if (dotData?.payload && dotData?.dataKey) {
                          const clickedVal = dotData.payload[dotData.dataKey];
                          const overlapping = activeSquads.filter(s => dotData.payload[s] === clickedVal && dotData.payload[s] != null);
                          setSquadPopup({ point: dotData.payload, squads: overlapping.length > 1 ? overlapping : [dotData.dataKey] });
                        }
                      }}}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              {squadPopup && (
                <BugPopup
                  point={squadPopup.point}
                  squads={squadPopup.squads}
                  allSquads={availableSquads}
                  bugItems={filteredBugItems}
                  resolvedDatesMap={resolvedDatesMap}
                  isResolved={isResolved}
                  onClose={() => setSquadPopup(null)}
                  priorityFilter={bugPriorityFilter}
                />
              )}
            </>
          ) : (
            <div className="text-gray-500">Pas assez de données pour tracer l'évolution par squad.</div>
          )}
        </ExpandableChartCard>
      </div>

      {/* Autres graphiques */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ExpandableChartCard title="PBI 26.1.0 par statut" normalHeight={320}>
          {(height) => pbiByStatusData.length ? (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={pbiByStatusData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-20} textAnchor="end" height={70} interval={0} />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value, name) => [value, name]} />
                <Bar dataKey="count" name="PBI" fill="#2563eb" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500">Aucun PBI 26.1.0 hors Done trouvé.</div>
          )}
        </ExpandableChartCard>

        <ExpandableChartCard title="Bugs 26.1.0 par statut" normalHeight={320}>
          {(height) => bugsByStatusData.length ? (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={bugsByStatusData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-20} textAnchor="end" height={70} interval={0} />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value, name) => [value, name]} />
                <Bar dataKey="count" name="Bugs" fill="#dc2626" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500">Aucun bug 26.1.0 hors Done trouvé.</div>
          )}
        </ExpandableChartCard>

        <ExpandableChartCard
          title="Évolution quotidienne"
          subtitle={`Tickets ouverts par jour depuis le 13/04 jusqu'à la livraison estimée (${deliveryDate.toLocaleDateString('fr-FR')}).`}
          normalHeight={340}
        >
          {(height) => dailyTimelineData.length ? (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={dailyTimelineData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <ReferenceLine
                  x={dailyTimelineData.find(row => row.isDeliveryDay)?.day}
                  stroke="#dc2626"
                  strokeDasharray="4 4"
                  label={{ value: 'Livraison cible', position: 'insideTopRight', fill: '#dc2626' }}
                />
                {DAILY_TYPE_BUCKETS.map((bucket, idx) => (
                  <Bar
                    key={bucket.dataKey}
                    dataKey={bucket.dataKey}
                    name={bucket.name}
                    fill={bucket.fill}
                    stackId="types"
                    radius={idx === DAILY_TYPE_BUCKETS.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500">Pas assez de dates pour tracer une évolution.</div>
          )}
        </ExpandableChartCard>

        <ExpandableChartCard title="Répartition par type" normalHeight={320}>
          {(height) => typeData.length ? (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={typeData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-20} textAnchor="end" height={70} interval={0} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {typeData.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500">Aucun ticket trouvé.</div>
          )}
        </ExpandableChartCard>
      </div>

      <ExpandableChartCard
        title="Types de tickets par area path"
        normalHeight={Math.max(360, topAreaTypeData.length * 42)}
        fullscreenHeight={Math.max(560, topAreaTypeData.length * 58)}
        extraContent={
          areaTypeData.length > topAreaTypeData.length ? (
            <div className="text-sm text-gray-500 mt-3">
              Affichage limité aux 12 area paths les plus chargés pour garder le graphique lisible.
            </div>
          ) : null
        }
      >
        {(height) => topAreaTypeData.length ? (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={topAreaTypeData} layout="vertical" margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="areaPath" type="category" width={220} />
              <Tooltip formatter={(value, name) => [value, name]} labelFormatter={(_, payload) => payload?.[0]?.payload?.fullAreaPath || ''} />
              <Legend />
              {typeKeys.map((typeKey, index) => (
                <Bar key={typeKey} dataKey={typeKey} stackId="types" fill={COLORS[index % COLORS.length]} radius={index === typeKeys.length - 1 ? [0, 6, 6, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-gray-500">Aucun ticket trouvé.</div>
        )}
      </ExpandableChartCard>
    </div>
  );
}
